/**
 * Performance shapes shared by the contract, server and web. The `performance` animal facet keeps the
 * keys MODULES.md promises (last_weight, weights.{birth,weaning,yearling}, adg, bcs) stable.
 */
import type { Quantity } from "../../../contract";

export const WEIGHT_CONTEXTS = {
  birth: "Birth weight", weaning: "Weaning weight", yearling: "Yearling weight", routine: "Routine weight",
  arrival: "Arrival weight", sale: "Sale weight", ship: "Shipping weight",
} as const;
export type WeightContext = keyof typeof WEIGHT_CONTEXTS;
export type Milestone = "birth" | "weaning" | "yearling";
export const MILESTONES: Milestone[] = ["birth", "weaning", "yearling"];

export const WEIGH_METHODS = { scale: "Scale", tape: "Weight tape", estimate: "Visual estimate" } as const;
export type WeighMethod = keyof typeof WEIGH_METHODS;

export const FILL = { full: "Full", shrunk: "Shrunk (off feed and water)" } as const;

export interface WeightData {
  weight?: Quantity;
  context?: WeightContext;
  method?: WeighMethod;
  condition?: keyof typeof FILL;
  shrink_pct?: number;
  reweigh?: boolean;
  group_code?: string;
  scale_ref?: string;
  reading_id?: string;
  stable?: boolean;
  adjusted_weight?: Quantity;
  adjusted_by?: string;
  ratio?: number;
  source_ref?: string;
  notes?: string;
}

export interface GroupWeightData {
  average: Quantity;
  head: number;
  total?: Quantity;
  context?: WeightContext;
  method?: WeighMethod;
  condition?: keyof typeof FILL;
  shrink_pct?: number;
  description?: string;
  notes?: string;
}

export type ScoreValue = "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9";

export interface MeasureData {
  bcs?: number;
  scrotal?: Quantity;
  hip_height?: Quantity;
  frame?: number;
  docility?: ScoreValue;
  udder?: ScoreValue;
  teat?: ScoreValue;
  foot_angle?: number;
  claw_set?: number;
  muscle?: number;
  pelvic_area?: number;
  measured_by?: string;
  notes?: string;
}

export type AreaUnit = "sq_in" | "sq_cm";

export interface UltrasoundData {
  imf?: number;
  rea?: number;
  rea_unit?: AreaUnit;
  rib_fat?: Quantity;
  rump_fat?: Quantity;
  scan_weight?: Quantity;
  technician?: string;
  lab?: string;
  sample_id?: string;
  imf_ratio?: number;
  rea_ratio?: number;
  fat_ratio?: number;
  group_code?: string;
  source_ref?: string;
  notes?: string;
}

export const QUALITY_GRADES = {
  prime: "Prime", choice_upper: "Upper 2/3 Choice", choice_low: "Low Choice", choice: "Choice", select: "USDA Select", standard: "Standard",
  commercial: "Commercial", utility: "Utility", no_roll: "No roll (ungraded)",
} as const;
export const MARBLING = {
  practically_devoid: "Practically devoid", traces: "Traces", slight: "Slight", small: "Small", modest: "Modest", moderate: "Moderate",
  slightly_abundant: "Slightly abundant", moderately_abundant: "Moderately abundant", abundant: "Abundant",
} as const;
export const MATCH_BASIS = { eid: "EID", registration: "Registration number", tag: "Ear tag", carcass_id: "Carcass number on the shipping list", manual: "Picked by hand" } as const;

export interface CarcassData {
  hcw?: Quantity;
  quality_grade?: keyof typeof QUALITY_GRADES;
  grade_as_written?: string;
  yield_grade?: number;
  rea?: number;
  rea_unit?: AreaUnit;
  marbling?: keyof typeof MARBLING;
  back_fat?: Quantity;
  kph?: number;
  live_weight?: Quantity;
  dressing_pct?: number;
  plant?: string;
  carcass_id?: string;
  lot?: string;
  match_basis?: keyof typeof MATCH_BASIS;
  source_ref?: string;
  notes?: string;
}

export const INTAKE_BASIS = { measured: "Measured for this animal", allocated: "Shared out from pen feed" } as const;

export interface IntakeData {
  as_fed?: Quantity;
  dry_matter?: Quantity;
  basis: keyof typeof INTAKE_BASIS;
  days?: number;
  source?: string;
  notes?: string;
}

// ---------------------------------------------------------------- facet state

export interface At<T = number> { value: T; on: string; event_id: string }
export interface QuantityAt { value: number; unit: string; on: string; event_id: string }

export type FormulaId = "bif_205" | "bif_205_age" | "bif_365" | "bif_365_age" | "bif_frame";

export interface CalcInputs {
  birth_weight?: { value: number; unit: string; on?: string; event_id?: string; assumed?: boolean };
  weaning_weight?: { value: number; unit: string; on: string; event_id: string };
  yearling_weight?: { value: number; unit: string; on: string; event_id: string };
  weaning_adjusted?: number;
  birth_date?: string;
  dam_age_years?: number;
  aod_lb?: number;
  sex?: "male" | "female";
  frame_sex?: "bull" | "heifer";
  hip_height_in?: number;
  age_days?: number;
  days_between?: number;
}

/** A value Hondo worked out, with the rule, its version and every input, so it can always be explained. */
export interface Calculated {
  value: number;
  unit: "lb" | "score";
  basis: "calculated";
  formula: FormulaId;
  version: number;
  age_days?: number;
  inputs: CalcInputs;
  computed_on: string;
}

export type MissingReason =
  | "birth_date" | "birth_weight" | "dam_age" | "sex" | "age_range" | "estimate" | "date" | "weaning" | "too_close" | "steer";

export interface MilestoneWeight {
  value: number;
  unit: string;
  on: string;
  event_id: string;
  method?: WeighMethod;
  group_code?: string;
  /** Where the actual weight came from: a weight record or a calving record. */
  source?: "weight" | "calving";
  approx_date?: boolean;
  job_id?: string | null;
  /** Complete adjusted value (BIF, with age-of-dam). */
  adjusted?: Calculated;
  /** Age-adjusted only, without the age-of-dam adjustment. Not a full adjusted weight. */
  age_adjusted?: Calculated;
  /** Why `adjusted` is missing. */
  missing?: MissingReason[];
}

/** An adjusted value someone else calculated (breed association, lab). Never overwritten by Hondo's own. */
export interface ImportedResult { value: number; unit: string; on: string | null; by?: string; ratio?: number; event_id: string }

export interface Gain { value: number; unit: "lb_per_day" | "kg_per_day"; from: string; to: string; days: number }

export interface WeightPoint { value: number; unit: string; on: string; event_id: string; context?: WeightContext; estimate?: boolean; job_id?: string | null }

export interface Scores {
  scrotal?: QuantityAt;
  hip_height?: QuantityAt;
  frame?: At;
  frame_calc?: Calculated;
  docility?: At;
  udder?: At;
  teat?: At;
  foot_angle?: At;
  claw_set?: At;
  muscle?: At;
  pelvic_area?: At;
}

export interface UltrasoundState {
  on: string; event_id: string; imf?: number; rea?: { value: number; unit: AreaUnit }; rib_fat?: Quantity; rump_fat?: Quantity; scan_weight?: Quantity;
  imf_ratio?: number; rea_ratio?: number; fat_ratio?: number;
}

export interface CarcassState {
  on: string; event_id: string; hcw?: Quantity; quality_grade?: string; yield_grade?: number; marbling?: string; rea?: { value: number; unit: AreaUnit };
  back_fat?: Quantity; plant?: string;
}

export interface PerformanceState {
  last_weight?: { value: number; unit: string; on: string; event_id: string; context?: WeightContext; estimate?: boolean };
  weights: Partial<Record<Milestone, MilestoneWeight>>;
  imported?: Partial<Record<Milestone, ImportedResult>>;
  /** Between the last two weights at least a week apart (estimates left out). */
  adg?: Gain;
  /** From the recorded birth weight to the last weight. */
  adg_birth?: Gain;
  bcs?: { score: number; on: string; event_id: string };
  scores: Scores;
  ultrasound?: UltrasoundState;
  carcass?: CarcassState;
  /** Oldest first, at most 120 points. */
  history: WeightPoint[];
  weighed: number;
  /** When adjusted values were last worked out from the animal's record (birth date, dam). */
  computed?: { on: string; version: number };
}

export interface GroupWeightState {
  last: { average: Quantity; head: number; total?: Quantity; context?: WeightContext; on: string; event_id: string };
  history: Array<{ average: Quantity; head: number; on: string; context?: WeightContext; event_id: string }>;
}

export interface PerformanceSettings {
  assumed_birth_weight?: Quantity;
  outlier_pct?: number;
}

/** Inputs from the animal's own record (not events) that adjusted values need. */
export interface AnimalInputs {
  birth_date: string | null;
  birth_precision: string | null;
  sex: string;
  castrated: boolean;
  dam_birth_date: string | null;
}
